import { useEffect, useRef, useState } from "react";
import { LocateFixed, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

// Where the confirmer was: typed suburb/town (with suggestions) or their phone's location once. Only the suburb is kept.
export const PlacePicker = ({ value, onChange, testid }) => {
  const [q, setQ] = useState(value?.place || "");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef();
  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 3 || (value?.lat != null && q === value.place)) { setItems([]); return undefined; }
    timer.current = setTimeout(() => api.get("/geo/places", { params: { q } }).then((r) => setItems(r.data.items)).catch(() => setItems([])), 450);
    return () => clearTimeout(timer.current);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (p) => { onChange(p); setQ(p.place); setItems([]); };
  const locate = () => {
    if (!navigator.geolocation) { toast.error("Your browser can't share location. Type your suburb instead."); return; }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try { const { data } = await api.get("/geo/reverse", { params: { lat: pos.coords.latitude, lng: pos.coords.longitude } }); pick(data); } catch (e) { toast.error(errMsg(e)); }
      setBusy(false);
    }, () => { setBusy(false); toast.error("Location wasn't shared. Type your suburb instead."); }, { maximumAge: 600000, timeout: 10000 });
  };
  return (
    <div className="relative space-y-1.5" data-testid={testid}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => { setQ(e.target.value); onChange(e.target.value ? { place: e.target.value } : null); }} placeholder="Where were you? Suburb or town (optional)"
            className="w-full rounded-xl border border-border py-2 pl-8 pr-3 text-sm outline-none focus:ring-2 focus:ring-slate-200" data-testid={`${testid}-input`} />
        </div>
        <button type="button" onClick={locate} disabled={busy} className="inline-flex shrink-0 items-center gap-1 rounded-xl border border-border px-2.5 text-xs text-slate-600 hover:border-slate-400" data-testid={`${testid}-locate`}>
          <LocateFixed size={14} />{busy ? "Finding…" : "Use my location"}</button>
      </div>
      {items.length > 0 && (
        <ul className="absolute z-20 w-full overflow-hidden rounded-xl border border-border bg-white shadow-lg" data-testid={`${testid}-suggestions`}>
          {items.map((p, i) => <li key={p.place}><button type="button" onClick={() => pick(p)} className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50" data-testid={`${testid}-option-${i}`}>{p.place}</button></li>)}
        </ul>)}
      <p className="text-[11px] text-slate-400">Only your suburb or town is kept, never your exact spot, and it isn't shown to anyone. It's used to count confirmations from people nearby.</p>
    </div>
  );
};
